import { Download } from '@mui/icons-material';
import { Box, Button, Typography } from '@mui/material';
import { concat, get } from 'lodash';
import React, { FC, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import MUIAlert from 'src/components/ui-component/mui-alert/MUIAlert';
import GetRecord from 'src/lib/api/GetRecord';
import { APIS, METHOD_POST } from 'src/lib/constants/apis';
import { TEMPLATES_SERVICE_CLIENT_ID, TEMPLATES_SERVICE_PASSWORD, TEMPLATES_SERVICE_SERVER_ID, TEMPLATES_URL } from 'src/lib/constants/url';
import { ForceTrue, ValidDate } from 'src/lib/validation/ValidationTypes';
import { InhaltTrenner } from 'src/pages/private/online-antrag/antrag-components/Wrapper';
import CreateForm from '../../../../../../../components/ui-component/form-component/create-form/CreateForm';
import { useApplication } from '../../../../../../../context/ApplicationContext';
import {
	ALS_ERLEDIGT_MARKIEREN_LABEL,
	ANTRAGSINFORMATIONEN_VORZEITIGER_MASSNAHMEBEGINN,
	EINREICHUNG_DER_UNTERLAGEN,
	PRUEF_ASSISTENZ_STATUS,
	TEMPLATES_VORZEITIGER_MASSNAHME,
} from '../../../../../../../lib/constants/constant';
import { FormStrucItem } from '../../../../../../../lib/types/types';
import { checkReadOnlyInUeberarbeitung, enumT, isTrue, transformValues } from '../../../../../../../utils/helper';
import PruefAssistenz from '../../../../antrag-components/PruefAssistenz';
import ApplicationHeading from '../../../../application-heading/ApplicationHeading';

// import schemas
import AntragSchema from '../../../../../../../lib/paths/antrag_FULL_Paths.json';
import FoerderProgrammSchema from '../../../../../../../lib/paths/foerderprogramm_FULL_Paths.json';
const P = AntragSchema.attributes;
const FP_P = FoerderProgrammSchema.attributes;

const VorzeitigeMassnahmeGlobal: FC<{ hasComment?: boolean }> = ({ hasComment }) => {
	const location = useLocation();
	const { application, setApplicationProperty, saveApplication, readOnly } = useApplication();
	const updatedReadOnly = checkReadOnlyInUeberarbeitung(application, readOnly, hasComment);

	const showWarningForDate = useMemo(() => {
		const projektLaufzeitVon = get(application, P.Allgemein_Geplante_Projektlaufzeit_von.path, null);

		if (projektLaufzeitVon) {
			const vorzeitigerVorhabenbeginn = get(application, P.Vorzeitiger_Vorhabenbeginn.schema.attributes.Vorzeitiger_Vorhabenbeginn_Ab.path, null);

			if (projektLaufzeitVon !== vorzeitigerVorhabenbeginn) {
				return true;
			}
		}
		return false;
	}, [application]);

	const handlegeneratePDF = async () => {
		const templateUrl = TEMPLATES_URL + TEMPLATES_VORZEITIGER_MASSNAHME.default;
		await saveApplication(false, application, false);

		let res = await GetRecord(
			APIS.Antrag,
			get(application, 'id'),
			{ customPopulate: get(location, 'state.antragsformular', 'FULL') },
			// get(POP_LEVEL, `${get(location, 'state.api').replace(/.$/, '')}.${get(location, 'state.antragsformular', 'FULL')}`)
		);
		// const appRes = removeNullDeep(removeDataKeys(get(res, 'data', [])));
		const enumTranslatedData = transformValues(res, enumT);

		let jsonString = JSON.stringify(enumTranslatedData);
		let jsonBlob = new Blob([jsonString], { type: 'application/json' });
		let pdfData = new FormData();
		pdfData.append('data', jsonBlob, 'antrag_template_daten.json');
		let pdfRes = await fetch(templateUrl, {
			method: METHOD_POST,
			headers: {
				Authorization: 'Basic ' + btoa(TEMPLATES_SERVICE_PASSWORD),
				'X-Server-Id': TEMPLATES_SERVICE_SERVER_ID,
				'X-Client-Id': TEMPLATES_SERVICE_CLIENT_ID,
			},
			body: pdfData,
		});
		if (pdfRes) {
			const blob = await pdfRes.blob();
			const objectUrl = URL.createObjectURL(blob);

			const link = document.createElement('a');
			link.href = objectUrl;
			link.download = `Vorzeitiger Vorhabenbeginn.pdf`;
			document.body.appendChild(link);
			link.click();
			link.remove();
			URL.revokeObjectURL(objectUrl);
		}
	};

	const showMassnahmeFeischaltungFeld: boolean = useMemo(() => {
		const foerderprogram = get(application, P.Foerderprogramm.path, {});

		return (
			get(foerderprogram, FP_P.Antragsinformationen_Vorzeitiger_Massnahmebeginn.path) ===
			ANTRAGSINFORMATIONEN_VORZEITIGER_MASSNAHMEBEGINN.AbAntragstellungMoeglich
		);
	}, [application]);

	const EinreichungDerUnterlagen = get(application, P.Foerderprogramm.schema.attributes.Konfiguration_Einreichung_Der_Antragsunterlagen.path, '');

	return (
		<>
			<ApplicationHeading title='Vorzeitiger Vorhabenbeginn' />

			<Typography variant='h4' component={'h2'}>
				Antrag auf „Genehmigung zum vorzeitigen Vorhabenbeginn“ gemäß Nr. 1.3 der Verwaltungsvorschriften zu § 44 Landeshaushaltsordnung (VV-LHO)
			</Typography>

			<Box sx={{ mt: '1rem' }}>
				<Typography>
					In begründeten Ausnahmen kann ein vorzeitiger Maßnahmenbeginn gewährt werden. Mit der Projektbearbeitung darf frühestens mit
					Inkrafttreten einer Zusage hierzu begonnen werden. Sollte dies auf Ihr Projekt zutreffen, wählen Sie bitte diese Option aus und folgen
					Sie anschließend den angegebenen Schritten:
				</Typography>
			</Box>

			<PruefAssistenz
				statusPath={P.Vorzeitiger_Vorhabenbeginn.schema.attributes.Vorzeitiger_Vorhabenbeginn_Pruefung_Pruefstatus.path}
				hinweisPath={P.Vorzeitiger_Vorhabenbeginn.schema.attributes.Vorzeitiger_Vorhabenbegin_hinnweis_Fuer_Den_Antragsteller.path}
				ueberarbeitetAmPath={P.Vorzeitiger_Vorhabenbeginn.schema.attributes.Vorzeitiger_Vorhabenbeginn_Pruefung_Ueberarbeitet_Am.path}
				erledigtPath={P.Vorzeitiger_Vorhabenbeginn.schema.attributes.Vorzeitiger_Vorhabenbeginn_Pruefung_Erledigt.path}
				formStruc={formStrucPruefAssistenz(application)}
			/>

			{showMassnahmeFeischaltungFeld && (
				<>
					<CreateForm
						formStruc={formStruc()}
						formValues={application}
						updateFormValuesFunc={setApplicationProperty}
						readOnly={updatedReadOnly}
					/>
					<Typography sx={{ mt: '1rem' }}>
						<strong>Bitte beachten Sie:</strong> Der Antrag auf "Genehmigung zum vorzeitigen Vorhabenbeginn" kann frühestens gleichzeitig mit
						dem "Antrag auf Förderung" eingereicht werden.{' '}
					</Typography>
				</>
			)}

			{isTrue(get(application, P.Allgemein_Vorzeitiger_Vorhabenbeginn_Freigeschaltet.path)) && (
				<>
					<InhaltTrenner dotted={true} marginBottom='1rem' />
					<CreateForm
						formStruc={formStrucBegruendung()}
						formValues={application}
						updateFormValuesFunc={setApplicationProperty}
						readOnly={updatedReadOnly}
					/>

					<InhaltTrenner dotted={true} marginBottom='1rem' />
					{showWarningForDate && (
						<MUIAlert
							infoType={'warning'}
							message={
								'Das Datum des geplanten vorzeitigen Vorhabenbeginns stimmt nicht mit dem geplanten Projektstart überein. Bitte überprüfen Sie Ihre Angaben.'
							}
						/>
					)}
					<Typography>
						<strong>Bitte geben Sie das Datum des geplanten vorzeitigen Vorhabenbeginns an.</strong>
					</Typography>
					<CreateForm
						formStruc={formStrucDatum()}
						formValues={application}
						updateFormValuesFunc={setApplicationProperty}
						readOnly={updatedReadOnly}
					/>

					<InhaltTrenner dotted={true} marginBottom='1rem' />
					<Typography>
						{' '}
						<strong>Erklärungen der Antragsteller / des Antragstellers zum geplanten vorzeitigen Vorhabenbeginn.</strong>
					</Typography>
					<CreateForm
						formStruc={formStrucErklaerung()}
						formValues={application}
						updateFormValuesFunc={setApplicationProperty}
						readOnly={updatedReadOnly}
					/>

					<InhaltTrenner dotted={false} marginBottom='1rem' />
					<ApplicationHeading title='Antrag auf Genehmigung zum vorzeitigen Vorhabenbeginn einreichen' />

					<Typography>
						Bitte berücksichtigen Sie, dass Ihr Antrag der Genehmigung zum vorzeitigen Vorhabenbeginn erst offiziell eingereicht ist, wenn er
						gemeinsam mit dem vollständigen Antrag eingereicht wurde.
						<br />
						<br /> Mit der Projektbearbeitung darf frühestens mit Inkrafttreten einer Zusage begonnen werden. Um eine schnellstmögliche
						Bearbeitung Ihres Antrags zu ermöglichen, folgen Sie bitte diesen Schritten:
					</Typography>

					<Typography sx={{ mt: '2rem' }}>
						<strong>Laden Sie hier das für Sie vorbereitete Antragsformular herunter und unterschreiben Sie den Antrag</strong>{' '}
						{EinreichungDerUnterlagen === EINREICHUNG_DER_UNTERLAGEN.digital ?
							' (eigenhändige Unterschrift oder elektronisch per eingescannter Signatur). Bitte speichern Sie den unterzeichneten Antrag anschließend als pdf-Datei ab.'
						:	null}
					</Typography>
					<Button variant={'outlined'} startIcon={<Download />} onClick={() => handlegeneratePDF()} sx={{ maxWidth: 'fit-content', mt: '1rem' }}>
						Antrag auf vorzeitigen Vorhabenbeginn herunterladen
					</Button>

					<InhaltTrenner dotted={false} marginBottom='1rem' />
					<Typography>
						<strong>Bitte laden Sie hier Ihren unterschriebenen Antrag als pdf-Dokument hoch:</strong>
					</Typography>
					<Box sx={{ mt: '1rem' }}>
						<CreateForm
							formStruc={formStrucUpload()}
							formValues={application}
							updateFormValuesFunc={setApplicationProperty}
							readOnly={updatedReadOnly}
						/>
					</Box>
				</>
			)}
		</>
	);
};

export default VorzeitigeMassnahmeGlobal;

// autofold/fold_next
const formStrucPruefAssistenz = (application: any) => {
	let struc: FormStrucItem[] = [
		{
			fields: [
				{
					...P.Vorzeitiger_Vorhabenbeginn.schema.attributes.Vorzeitiger_Vorhabenbeginn_Pruefung_Erledigt,
					label: ALS_ERLEDIGT_MARKIEREN_LABEL,
					validation:
						(
							get(application, P.Vorzeitiger_Vorhabenbeginn.schema.attributes.Vorzeitiger_Vorhabenbeginn_Pruefung_Pruefstatus.path) ===
							PRUEF_ASSISTENZ_STATUS.uberarbeitung
						) ?
							[{ validateFunction: ForceTrue }]
						:	[],
				},
			],
		},
	];
	return struc;
};

const formStruc = () => {
	let struc: FormStrucItem[] = [
		{
			fields: [
				{
					...P.Allgemein_Vorzeitiger_Vorhabenbeginn_Freigeschaltet,
					label: 'Ich / Wir bitte(n) um Erteilung einer „Genehmigung zum vorzeitigen Vorhabenbeginn“ zum geplanten Projektstart.',
				},
			],
			userProps: { direction: 'column', columnToDivide: 1 },
		},
	];
	return struc;
};
const formStrucBegruendung = () => {
	let struc: FormStrucItem[] = [
		{
			fields: [
				{
					...P.Vorzeitiger_Vorhabenbeginn.schema.attributes.Vorzeitiger_Vorhabenbeginn_Gruende,
					maxCharLength: 1000,
				},
			],
			userProps: { direction: 'column', columnToDivide: 1 },
		},
	];
	return struc;
};
const formStrucDatum = () => {
	let struc: FormStrucItem[] = [
		{
			fields: [
				{
					...P.Vorzeitiger_Vorhabenbeginn.schema.attributes.Vorzeitiger_Vorhabenbeginn_Ab,
					hideLabel: true,
					validation: [{ validateFunction: ValidDate }],
				},
			],
			userProps: { direction: 'column', columnToDivide: 4 },
		},
	];
	return struc;
};
const formStrucErklaerung = () => {
	let struc: FormStrucItem[] = [
		// Laut Ticket HA-2110, die Validierung entfernt
		// Aber erstmal auskommentiert lassen, bis es abgenommen wird.
		// HA ändern die Bedingungen schon wieder öfter
		{
			fields: [
				{
					...P.Vorzeitiger_Vorhabenbeginn.schema.attributes.Vorzeitiger_Vorhabenbeginn_Erklaerungen_Block_4_Zelle_4,
					label: 'Ich/Wir erklären, dass mit der Maßnahme noch nicht begonnen wurde.',
					// validation: [{ validateFunction: ForceTrue }],
				},
				{
					...P.Vorzeitiger_Vorhabenbeginn.schema.attributes.Vorzeitiger_Vorhabenbeginn_Erklaerungen_Block_1_Zelle_1,
					// validation: [{ validateFunction: ForceTrue }],
				},
				{
					...P.Vorzeitiger_Vorhabenbeginn.schema.attributes.Vorzeitiger_Vorhabenbeginn_Erklaerungen_Block_2_Zelle_1,
					// validation: [{ validateFunction: ForceTrue }],
				},
				{
					...P.Vorzeitiger_Vorhabenbeginn.schema.attributes.Vorzeitiger_Vorhabenbeginn_Erklaerungen_Block_5_Zelle_5,
					// validation: [{ validateFunction: ForceTrue }],
				},
				{
					...P.Vorzeitiger_Vorhabenbeginn.schema.attributes.Vorzeitiger_Vorhabenbeginn_Erklaerungen_Block_3_Zelle_3,
					// validation: [{ validateFunction: ForceTrue }],
				},
			],
			userProps: { direction: 'column', columnToDivide: 1 },
		},
	];
	return struc;
};
const formStrucUpload = () => {
	let struc: FormStrucItem[] = [
		{
			fields: [{ ...P.Vorzeitiger_Vorhabenbeginn.schema.attributes.Vorzeitiger_Vorhabenbeginn_Unterschriebener_Antrag, hideLabel: true }],
		},
	];
	return struc;
};

export const getFormStrucVorzeitigeMassnahmeGlobal = (application: any) => {
	let result: any[] = [];

	if (isTrue(get(application, P.Allgemein_Vorzeitiger_Vorhabenbeginn_Freigeschaltet.path))) {
		result = concat(formStruc(), formStrucBegruendung(), formStrucDatum(), formStrucErklaerung(), formStrucUpload());
	}
	const isHinweisFieldRequired =
		get(application, P.Vorzeitiger_Vorhabenbeginn.schema.attributes.Vorzeitiger_Vorhabenbegin_hinnweis_Fuer_Den_Antragsteller.path) &&
		!isTrue(get(application, P.Vorzeitiger_Vorhabenbeginn.schema.attributes.Vorzeitiger_Vorhabenbeginn_Pruefung_Erledigt.path));

	if (isHinweisFieldRequired) {
		return concat(formStrucPruefAssistenz(application), result);
	}
	return result;
};
